import { useState, useEffect, useMemo, useCallback } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { Navigate, useNavigate } from 'react-router-dom'

import useInterval from '@/hooks/interval'

import { PageName } from '@/enums/pageName'
import { getPath } from '@/utils/urlUtils'

import { TextButton } from '@/components/buttons'
import { TextHTML } from '@/components/typography'

import { TSendVerificationCodePayload, TVerificationV2Payload } from '@/api/types'
import { emailKey } from '@/auth/types'
import { useAuth } from '@/auth/useAuth'
import { useSession } from '@/core/session/useSession'
import { trackPageView, trackVerificationSuccess, trackVerificationFailed } from '@/events/events'

import { CodeForm } from './forms/codeForm'
import { EmailForm } from './forms/emailForm'
import { TEmailFormValues, TEmailVerificationFormValues, TVerificationPageV2Props } from './types'

const RESEND_COOLDOWN_LENGTH = 59

export const VerificationPageV2 = ({
  pageName,
  verificationPage,
  getItem,
  config,
}: TVerificationPageV2Props) => {
  const { user, loginV2, sendVerificationCode } = useAuth()
  const sessionContext = useSession()

  const [isLoading, setIsLoading] = useState(false)
  const [requestError, setRequestError] = useState<string>()
  const [email, setEmail] = useState<string | undefined | null>(getItem(emailKey))
  const [isEmailConfirmed, setIsEmailConfirmed] = useState<boolean>(false)
  const navigate = useNavigate()

  const {
    emailTitle,
    emailFormDescription,
    emailPlaceholder,
    emailButtonName,
    codeTitle,
    codeFormDescription,
    codePlaceholder,
    codeButtonName,

    pageTitle,
    contactsText,
    nextUrl,
    resendButtonText,
    resendCooldownText,
  } = verificationPage

  const [resendCooldown, setResendCooldown] = useState<number>(0)

  useInterval(
    () => {
      setResendCooldown((prevState) => prevState - 1)
    },
    resendCooldown > 0 ? 1000 : null
  )

  const [cooldownTimerPrefixText, cooldownTimerPostfixText] = useMemo<string[]>(() => {
    return resendCooldownText.split('{timer}')
  }, [resendCooldownText])

  useEffect(
    () => {
      trackPageView(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        sessionContext,
        user
      )
      document.title = pageTitle
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  const sendVerificationEmail: SubmitHandler<TEmailFormValues> = useCallback(
    async (data) => {
      if (isLoading) {
        return
      }
      setIsLoading(true)

      const payload: TSendVerificationCodePayload = {
        email: data.email,
        page_set_id: config.PAGE_SET_ID,
      }

      try {
        await sendVerificationCode(payload)
        setEmail(data.email)
        setIsLoading(false)
        setIsEmailConfirmed(true)
        setResendCooldown(RESEND_COOLDOWN_LENGTH)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        setIsLoading(false)
        let errorMessage = error.response?.data?.error
        if (!errorMessage) {
          errorMessage = 'Something went wrong. Please, try again'
        }
        setRequestError(errorMessage)
      }
    },
    [isLoading, config, sendVerificationCode]
  )

  const verify: SubmitHandler<TEmailVerificationFormValues> = useCallback(
    async (data) => {
      if (!email || !isEmailConfirmed || isLoading) {
        return
      }
      setIsLoading(true)

      const payload: TVerificationV2Payload = {
        email: email,
        page_set_id: config.PAGE_SET_ID,
        code: data.code,
      }

      loginV2(payload).then(
        (result) => {
          trackVerificationSuccess(
            pageName,
            config.PARTNER_ID,
            config.APP_ID,
            config.PAGE_SET_ID,
            sessionContext,
            user
          )

          if (result.state === 'has_partner_account') {
            navigate(getPath(PageName.alreadyHasAccount))
          } else {
            if (nextUrl) {
              navigate(nextUrl)
            } else {
              navigate(getPath(PageName.paymentSuccess))
            }
          }
        },
        (error) => {
          setIsLoading(false)
          let errorMessage = error.response.data?.error
          if (!errorMessage) {
            errorMessage = 'Something went wrong. Please, try again'
          }
          setRequestError(errorMessage)
          trackVerificationFailed(
            pageName,
            config.PARTNER_ID,
            config.APP_ID,
            config.PAGE_SET_ID,
            sessionContext,
            user
          )
        }
      )
    },
    [
      email,
      pageName,
      isEmailConfirmed,
      isLoading,
      setIsLoading,
      config,
      loginV2,
      sessionContext,
      user,
      navigate,
      nextUrl,
      setRequestError,
    ]
  )

  const resend = useCallback(() => {
    if (!email) {
      return
    }
    sendVerificationEmail({ email })
  }, [email, sendVerificationEmail])

  if (user) {
    return <Navigate to={nextUrl} replace />
  }

  const className = `page page-${PageName.verification} page-${pageName}`

  const cooldownPrefix = resendCooldown < 10 ? '0' : ''
  const resendTimerTxt = `00:${cooldownPrefix}${resendCooldown.toString()}`
  let resendBlock
  if (isEmailConfirmed && email) {
    resendBlock = (
      <div className="resend-container">
        {resendCooldown ? (
          <div className="resend-cooldown-text">
            {cooldownTimerPrefixText}
            <span className="timer-text">{resendTimerTxt}</span>
            {cooldownTimerPostfixText}
          </div>
        ) : (
          <TextButton disabled={isLoading} onClick={resend}>
            {resendButtonText}
          </TextButton>
        )}
      </div>
    )
  }

  return (
    <div className={className}>
      {!isEmailConfirmed && (
        <EmailForm
          email={email}
          title={emailTitle}
          formDescription={emailFormDescription}
          buttonName={emailButtonName}
          isLoading={isLoading}
          inputPlaceholder={emailPlaceholder}
          errorMessage={requestError}
          onSubmit={sendVerificationEmail}
        />
      )}
      {isEmailConfirmed && (
        <CodeForm
          email={email}
          title={codeTitle}
          formDescription={codeFormDescription}
          buttonName={codeButtonName}
          isLoading={isLoading}
          inputPlaceholder={codePlaceholder}
          errorMessage={requestError}
          onSubmit={verify}
        />
      )}

      {resendBlock}
      <div className="contacts-container">
        <TextHTML title={contactsText} />
      </div>
    </div>
  )
}
